// Wohler Chemical — Categories Database
// Maps compounds to therapeutic research programs (A-J + K from Master Strategy File)
// 12 active categories — Phase 5C expansion: 119 stub compounds added from master RC list

export const categories = [
  {
    id: "tryptamines",
    name: "Tryptamine Analogs",
    program: "Program B — Tryptamine Space",
    icon: "🧬",
    color: "#4f46e5",
    colorLight: "#eef2ff",
    colorBorder: "#c7d2fe",
    description: "Serotonergic research compounds targeting 5-HT2A/5-HT2C pathways for depression, anxiety, PTSD, addiction, and end-of-life distress research.",
    compoundIds: [
      // Wohler flagship
      "4-aco-met",
      // Full research entries
      "4-ho-met", "4-aco-det", "4-aco-dpt", "4-aco-ept", "4-ho-det",
      "4-aco-mpt", "4-ho-mipt", "5-meo-nmt",
      // Master-list stubs
      "4-ho-mpt", "4-ho-ept", "4-ho-dpt", "4-aco-mipt",
      "5-meo-met", "5-meo-dalt", "norpsilocin-analogs", "aeruginascin-analogs"
    ]
  },
  {
    id: "mitragynine",
    name: "Mitragynine / MGM Analogs",
    program: "Program A — Kratom / Harm Reduction",
    icon: "🌿",
    color: "#059669",
    colorLight: "#ecfdf5",
    colorBorder: "#a7f3d0",
    description: "Kratom-derived and fluorinated mitragynine analogs for opioid-use disorder, pain research, and functional-bias mu-opioid receptor studies.",
    compoundIds: [
      // Full research entries
      "7-oh-mitragynine", "mgm-9", "mgm-15", "mgm-16", "sc13",
      "mitragynine", "mitragynine-pseudoindoxyl", "speciociliatine", "speciogynine",
      // Master-list stubs
      "paynantheine", "corynantheidine", "corynoxine-a",
      "mitragynine-oxindole-analogs", "fluorocorynantheidine-analogs", "mitragynine-ester-analogs"
    ]
  },
  {
    id: "opioid",
    name: "Opioid / Biased MOR",
    program: "Program D — Biased MOR / Safer Opioid",
    icon: "🛡️",
    color: "#0f766e",
    colorLight: "#f0fdfa",
    colorBorder: "#99f6e4",
    description: "Biased mu-opioid agonists, peripheral opioids, and harm-reduction opioid-receptor research tools.",
    compoundIds: [
      "sr-17018", "conolidine", "corynoxine-b", "nop-mor-mixed", "dor-agonists", "peripheral-opioids",
      // Master-list stubs
      "sr-14968", "pzm21", "oliceridine-comparator", "brorphine-warning-class",
      "peripheral-kor-agonists", "cebranopadol-like", "peptide-opioid-ligands",
      "damgo-comparator", "u50488-comparator"
    ]
  },
  {
    id: "iboga",
    name: "Iboga Analogs",
    program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    icon: "🔄",
    color: "#7c3aed",
    colorLight: "#f5f3ff",
    colorBorder: "#ddd6fe",
    description: "Non-hallucinogenic iboga-inspired analogs for addiction treatment, alcohol use disorder, opioid use disorder, and neural plasticity research.",
    compoundIds: [
      "tabernanthalog", "ibogainalog", "18-mc",
      // Master-list stubs
      "noribogaine", "voacangine-analogs", "catharanthine-analogs",
      "coronaridine-analogs", "psychoplastogen-analogs"
    ]
  },
  {
    id: "dissociatives",
    name: "Dissociatives / NMDA",
    program: "Program F — Ketamine / Glutamate",
    icon: "⚡",
    color: "#0891b2",
    colorLight: "#ecfeff",
    colorBorder: "#a5f3fc",
    description: "NMDA receptor antagonist analogs and glutamate-pathway research compounds for treatment-resistant depression, PTSD, and neuroplasticity research.",
    compoundIds: [
      "deschloroketamine", "2-fdck", "fluorexetamine",
      // Master-list stubs
      "blixeprodil", "xw10508", "hnk-analogs", "norketamine-analogs",
      "mxe-comparator", "3meopcp-comparator", "memantine-analogs",
      "dxm-analogs", "sigma-nmda-hybrids", "ampa-potentiators"
    ]
  },
  {
    id: "entactogens",
    name: "MDMA-Adjacent Entactogens",
    program: "Program E — Entactogens",
    icon: "💗",
    color: "#db2777",
    colorLight: "#fdf2f8",
    colorBorder: "#fbcfe8",
    description: "Entactogen and MDMA-adjacent research compounds for AUD, PTSD, and couples-therapy research.",
    compoundIds: [
      "heal", "meai", "sdma", "r-mdma", "r-mddma",
      // Master-list stubs
      "mdai", "5-iai", "mbdb-analogs", "mde-analogs",
      "sert-biased-releasers", "low-dat-entactogens",
      "oxytocin-adjuncts", "ht1b-ht2a-social"
    ]
  },
  {
    id: "gabaergic",
    name: "GABAergic / Anxiolytics",
    program: "Anxiety Lane",
    icon: "🌊",
    color: "#0284c7",
    colorLight: "#f0f9ff",
    colorBorder: "#bae6fd",
    description: "GABA-B agonists, TSPO ligands, and novel GABA-A α2/3-selective anxiolytics for anxiety, insomnia, and AUD research.",
    compoundIds: [
      "phenibut", "f-phenibut", "etifoxine", "krm-ii-81",
      // Master-list stubs
      "grx-917", "alpha-gaba-pam-library", "tspo-ligands",
      "allopregnanolone-analogs", "pregnenolone-analogs",
      "baclofen-analogs", "pagoclone-like", "afobazole-analogs",
      "kava-lactone-analogs", "l-theanine-analogs"
    ]
  },
  {
    id: "cognitive",
    name: "Cognitive / Wakefulness",
    program: "Programs C + J — Wakefulness & Nootropic",
    icon: "🧠",
    color: "#b45309",
    colorLight: "#fffbeb",
    colorBorder: "#fde68a",
    description: "Novel nootropic, wakefulness, and neurogenic research compounds including modafinil analogs, ampakines, BDNF potentiators, and memory enhancers.",
    compoundIds: [
      "f-modafinil", "adrafinil", "sunifiram", "unifiram",
      "prl-8-53", "nsi-189", "modafinil-anti-inflammatory-11a-k",
      // Master-list stubs
      "modafinil-comparator", "armodafinil-comparator",
      "flmodafinil-lauflumide", "fladrafinil", "ce-123",
      "bromantane-analogs", "alpha7-nachr-pams", "ampakines",
      "pde4-cognition-tools", "h3-antagonists", "orexin-agonists"
    ]
  },
  {
    id: "pain",
    name: "Pain Research Compounds",
    program: "Pain / Non-Opioid Analgesia",
    icon: "💊",
    color: "#dc2626",
    colorLight: "#fef2f2",
    colorBorder: "#fecaca",
    description: "Non-opioid analgesic research tools targeting kappa-opioid, sigma-1, TAAR1, ion-channel, and endocannabinoid pathways.",
    compoundIds: [
      "salvinorin-a-analogs", "sigma-1-modulators", "taar1-agonists",
      // Master-list stubs
      "2mm-salvinorin-b", "eom-salvinorin-b", "sigma-2-ligands",
      "nav17-blockers", "nav18-blockers", "trpv1-antagonists", "trpa1-antagonists",
      "p2x3-antagonists", "n-ca-blockers", "faah-inhibitors", "magl-inhibitors",
      "pea-analogs", "ldn-analogs"
    ]
  },
  {
    id: "ed-sexual",
    name: "ED / Sexual Health",
    program: "Program G — ED / Sexual Health",
    icon: "❤️",
    color: "#e11d48",
    colorLight: "#fff1f2",
    colorBorder: "#fecdd3",
    description: "Melanocortin receptor agonists and PDE5/cNOS-modulating research compounds for erectile dysfunction and HSDD research.",
    compoundIds: [
      "pt-141", "icariin", "amino-tadalafil", "thio-tadalafil", "sildenafil-analogs",
      // Master-list stubs
      "tadalafil-analogs-class", "avanafil-analogs", "mirodenafil", "udenafil", "lodenafil",
      "slx-2101", "tpn171h", "icariside-ii", "baohuoside-analogs",
      "melanotan-ii", "oxytocin-analogs", "no-donor-adjuncts", "sgc-stimulators",
      "rhok-inhibitors", "endothelin-antagonists", "d4-agonists"
    ]
  },
  {
    id: "peptides",
    name: "Peptides / Longevity",
    program: "Program I — Peptide PK-Extension / Epitalon",
    icon: "🔬",
    color: "#6366f1",
    colorLight: "#eef2ff",
    colorBorder: "#c7d2fe",
    description: "Research-grade peptides including pineal-derived tetrapeptides and PK-extended analogs for longevity, circadian, and telomere research.",
    compoundIds: [
      "epitalon",
      // Master-list stubs
      "epithalamin-peptides", "epitalon-hl-extended", "epitalon-lipidated",
      "epitalon-albumin-binding", "epitalon-pegylated",
      "cjc-1295-comparator", "melatonin-peptide-hybrids", "dsip-analogs",
      "bpc-157", "thymosin-beta4", "kpv-analogs", "semax-selank", "glp1-platform"
    ]
  },
  {
    id: "cannabinoid",
    name: "Endocannabinoid / CB2",
    program: "Program K — Endocannabinoid & Anti-Inflammatory Cannabinoids",
    icon: "🍃",
    color: "#16a34a",
    colorLight: "#f0fdf4",
    colorBorder: "#bbf7d0",
    description: "Non-intoxicating cannabinoids and CB2-selective research compounds for neuroinflammation, chronic pain, anxiety, and immune research — a lower-risk university-friendly program.",
    compoundIds: [
      "cbd-research-grade", "cbg-research-grade", "hu-308", "jwh-133",
      // Master-list stubs
      "cb2-selective-class", "beta-caryophyllene-analogs",
      "cbd-analogs-class", "cbg-analogs-class", "cbc-analogs", "cbdv-analogs", "thcv-analogs",
      "naaa-inhibitors", "oea-analogs", "non-cb1-modulators",
      // Cross-listed from pain category (endocannabinoid system)
      "faah-inhibitors", "magl-inhibitors", "pea-analogs"
    ]
  },
  {
    id: "gabapentinoids",
    name: "Gabapentinoids / α2δ Ligands",
    program: "Program L — α2δ Calcium-Channel Ligands (Neuropathic Pain & Anxiety-Adjacent)",
    icon: "⚡",
    color: "#0369a1",
    colorLight: "#f0f9ff",
    colorBorder: "#bae6fd",
    description: "Gabapentin-like and pregabalin-like research compounds targeting α2δ voltage-gated calcium-channel auxiliary subunits for neuropathic pain, fibromyalgia, sleep research, anxiety-adjacent calming, and opioid-sparing analgesia — a non-opioid, non-benzodiazepine mechanism with strong university-research appeal.",
    compoundIds: [
      "gabapentin",
      "pregabalin",
      "mirogabalin",
      "atagabalin",
      "imagabalin",
      "gabapentin-enacarbil",
      "4-methylpregabalin",
      "alpha2delta-1-selective-class",
      "peripheral-alpha2delta-class"
    ]
  }
];

export function getCategory(id) {
  return categories.find(c => c.id === id) || null;
}

export function getAllCategories() {
  return categories;
}